/**
 * Resilia-style Landing Page interactions (lp-*)
 * Small, dependency-free custom elements. Progressive enhancement only:
 * every element renders meaningful static content without JS.
 *
 * Elements:
 *  <lp-countdown>   — timezone-aware promo countdown (hides/expires honestly)
 *  <lp-offer-cards> — offer selector; syncs qty/variant into the native
 *                     Horizon product form (no custom cart fetch)
 *  <lp-countup>     — IntersectionObserver count-up for stat bands
 *  <lp-video-card>  — click-to-play UGC video with offscreen pause
 *  <lp-delivery>    — merchant-configured delivery window renderer
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------------ */
/* Countdown                                                           */
/* ------------------------------------------------------------------ */

class LpCountdown extends HTMLElement {
  connectedCallback() {
    const ends = Date.parse(this.dataset.ends || '');
    if (Number.isNaN(ends)) {
      // No valid deadline configured — never show fake urgency.
      this.hidden = true;
      return;
    }
    this.endsAt = ends;
    // Markup ships hidden so no-JS visitors never see placeholder digits;
    // reveal only once JS has validated a real deadline.
    this.hidden = false;
    this.numRefs = {
      d: this.querySelector('[data-unit="d"]'),
      h: this.querySelector('[data-unit="h"]'),
      m: this.querySelector('[data-unit="m"]'),
      s: this.querySelector('[data-unit="s"]'),
    };
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const remaining = this.endsAt - Date.now();
    if (remaining <= 0) {
      clearInterval(this.timer);
      this.expire();
      return;
    }
    const s = Math.floor(remaining / 1000);
    const parts = {
      d: Math.floor(s / 86400),
      h: Math.floor((s % 86400) / 3600),
      m: Math.floor((s % 3600) / 60),
      s: s % 60,
    };
    for (const [unit, el] of Object.entries(this.numRefs)) {
      if (!el) continue;
      el.textContent = String(parts[unit]).padStart(2, '0');
      // Hide the days unit entirely when < 1 day remains.
      if (unit === 'd') el.closest('.lp-countdown__unit')?.toggleAttribute('hidden', parts.d === 0);
    }
  }

  expire() {
    const message = this.dataset.expiredMessage;
    const bar = this.closest('.lp-announce');
    if (this.dataset.expiredBehavior === 'message' && message && bar) {
      const msg = bar.querySelector('.lp-announce__msg');
      if (msg) msg.textContent = message;
      this.hidden = true;
    } else if (this.dataset.expiredBehavior === 'hide-section' && bar) {
      bar.closest('.shopify-section')?.setAttribute('hidden', '');
    } else {
      this.hidden = true;
    }
  }
}

/* ------------------------------------------------------------------ */
/* Offer cards                                                         */
/* ------------------------------------------------------------------ */

class LpOfferCards extends HTMLElement {
  connectedCallback() {
    this.status = this.querySelector('[data-lp-offer-status]');
    this.addEventListener('change', (e) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.name === this.radioName) this.applyOffer(input);
      if (input.hasAttribute('data-lp-refill')) this.applyRefill(input);
    });
    // Apply the default-checked offer on load so form state matches UI.
    const checked = this.querySelector(`input[name="${this.radioName}"]:checked`);
    if (checked) this.applyOffer(checked, { silent: true });
    const refill = this.querySelector('input[data-lp-refill]');
    if (refill) this.applyRefill(refill);
  }

  /**
   * Toggle the selling plan on the native form. Only acts when the product
   * actually has a selling plan configured (data-selling-plan-id present);
   * otherwise the checkbox is display-only.
   */
  applyRefill(checkbox) {
    this.renderPurchaseModePrices(checkbox.checked);

    const selected = this.querySelector(`input[name="${this.radioName}"]:checked`);
    if (selected) this.applyOffer(selected, { silent: true });
  }

  get radioName() {
    return this.dataset.radioName || 'lp-offer';
  }

  /** The native Horizon product form living in the same section. */
  get form() {
    const section = this.closest('.shopify-section');
    return section ? section.querySelector('product-form-component form') : null;
  }

  get subscribed() {
    return Boolean(this.querySelector('input[data-lp-refill]')?.checked);
  }

  applyOffer(input, { silent = false } = {}) {
    const form = this.form;
    if (!form) return;

    const quantity = parseInt(input.dataset.quantity || '1', 10);
    const variantId = input.dataset.variantId || '';

    const qtyInput = form.querySelector('input[name="quantity"]');
    if (qtyInput) {
      qtyInput.value = String(quantity);
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      // No visible quantity input — carry quantity via a hidden input.
      let hidden = form.querySelector('input[data-lp-quantity]');
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'quantity';
        hidden.dataset.lpQuantity = 'true';
        form.append(hidden);
      }
      hidden.value = String(quantity);
    }

    if (variantId) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = variantId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.applySelectedSellingPlan(input);
    this.updateSummaryPrice(input);

    const summaryBadge = this.closest('.shopify-section')?.querySelector('[data-lp-price-badge]');
    if (summaryBadge && input.dataset.summaryBadge) {
      summaryBadge.textContent = `FINAL SUMMER SALE · ${input.dataset.summaryBadge}`;
    }

    // Reflect tier state on the gift-unlock ladder, if present.
    const tier = parseInt(input.dataset.tier || '0', 10);
    this.querySelectorAll('[data-lp-unlock-tier]').forEach((row) => {
      const rowTier = parseInt(row.dataset.lpUnlockTier || '99', 10);
      const unlocked = rowTier <= tier;
      row.classList.toggle('is-unlocked', unlocked);
      const state = row.querySelector('[data-lp-unlock-state]');
      if (state) state.textContent = unlocked ? row.dataset.lpUnlockedLabel || '' : row.dataset.lpLockedLabel || '';
    });

    if (!silent && this.status) {
      this.status.textContent = input.dataset.announce || '';
    }
  }

  /** Apply the selected tier's real Shopify selling plan at checkout. */
  applySelectedSellingPlan(input) {
    const form = this.form;
    if (!form) return;

    const planId = this.subscribed ? input.dataset.sellingPlanId || '' : '';
    let plan = form.querySelector('input[name="selling_plan"]');

    if (planId) {
      if (!plan) {
        plan = document.createElement('input');
        plan.type = 'hidden';
        plan.name = 'selling_plan';
        form.append(plan);
      }
      plan.value = planId;
    } else if (plan) {
      plan.remove();
    }
  }

  /** Swap all tier cards between one-time and subscription prices. */
  renderPurchaseModePrices(subscribed) {
    const mode = subscribed ? 'Sub' : 'Once';

    this.querySelectorAll('input[data-tier]').forEach((input) => {
      const card = input.closest('.lp-offer');
      const price = input.dataset[`price${mode}`] || input.dataset.price || '';
      const compareAtPrice =
        input.dataset[`compareAtPrice${mode}`] || input.dataset.compareAtPrice || '';
      const priceElement = card?.querySelector('[data-lp-offer-price]');
      const compareElement = card?.querySelector('[data-lp-offer-compare]');

      if (priceElement && price) priceElement.textContent = price;
      if (compareElement) compareElement.textContent = compareAtPrice;
    });
  }

  /**
   * Keep the headline price tied to the selected bundle. Prices are rendered
   * by Liquid from the live Shopify variants, so the storefront never relies
   * on duplicated hard-coded dollar values.
   */
  updateSummaryPrice(input) {
    const summary = this.closest('.shopify-section')?.querySelector('product-price');
    if (!summary) return;

    const mode = this.subscribed ? 'Sub' : 'Once';
    const price = input.dataset[`price${mode}`] || input.dataset.price || '';
    const compareAtPrice =
      input.dataset[`compareAtPrice${mode}`] || input.dataset.compareAtPrice || '';
    const regular = summary.querySelector('.price__regular');
    const sale = summary.querySelector('.price__sale');
    const regularPrice = regular?.querySelector('.price');
    const salePrice = sale?.querySelector('.price-item--sale');
    const comparePrice = sale?.querySelector('.price-item--regular');
    const onSale = Boolean(price && compareAtPrice);

    if (regularPrice && price) regularPrice.textContent = price;
    if (salePrice && price) salePrice.textContent = price;
    if (comparePrice) comparePrice.textContent = compareAtPrice;

    regular?.classList.toggle('price__hidden', onSale);
    sale?.classList.toggle('price__hidden', !onSale);
  }
}

/* ------------------------------------------------------------------ */
/* Count-up stats                                                      */
/* ------------------------------------------------------------------ */

class LpCountup extends HTMLElement {
  connectedCallback() {
    this.target = parseFloat(this.dataset.target || '');
    if (Number.isNaN(this.target)) return;
    if (REDUCED_MOTION.matches) {
      this.render(this.target);
      return;
    }
    this.render(0);
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.observer.disconnect();
          this.animate();
        }
      },
      { threshold: 0.4 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    cancelAnimationFrame(this.raf);
  }

  animate() {
    const duration = 1400;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      this.render(this.target * eased);
      if (t < 1) this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }

  render(value) {
    const decimals = parseInt(this.dataset.decimals || '0', 10);
    this.textContent = Number(value).toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
}

/* ------------------------------------------------------------------ */
/* UGC video card                                                      */
/* ------------------------------------------------------------------ */

class LpVideoCard extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.lp-video-card__play');
    this.video = this.querySelector('video');
    if (!this.button || !this.video) return;

    this.button.addEventListener('click', () => this.toggle());

    // Pause when scrolled offscreen.
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting && !this.video.paused) this.video.pause();
        });
      },
      { threshold: 0.2 }
    );
    this.observer.observe(this);

    this.video.addEventListener('play', () => this.setPlaying(true));
    this.video.addEventListener('pause', () => this.setPlaying(false));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  toggle() {
    if (this.video.paused) {
      // Lazy-attach sources on first play.
      this.video.querySelectorAll('source[data-src]').forEach((s) => {
        s.src = s.dataset.src;
        s.removeAttribute('data-src');
      });
      if (this.video.readyState === 0) this.video.load();
      this.video.play();
    } else {
      this.video.pause();
    }
  }

  setPlaying(playing) {
    this.classList.toggle('is-playing', playing);
    this.button.querySelector('.lp-icon')?.toggleAttribute('hidden', playing);
    const label = playing ? this.button.dataset.pauseLabel : this.button.dataset.playLabel;
    if (label) this.button.setAttribute('aria-label', label);
  }
}

/* ------------------------------------------------------------------ */
/* Customer-story carousel                                             */
/* ------------------------------------------------------------------ */

class LpStoryCarousel extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-story-rail]');
    this.buttons = [...this.querySelectorAll('[data-story-page]')];
    if (!this.rail || this.buttons.length === 0) return;

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.goTo(Number(button.dataset.storyPage || 0)));
    });

    this.rail.addEventListener('scroll', () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateActive();
      });
    });
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.scrollFrame);
  }

  goTo(page) {
    const maxScroll = Math.max(this.rail.scrollWidth - this.rail.clientWidth, 0);
    this.rail.scrollTo({
      left: page === 0 ? 0 : maxScroll,
      behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
    });
    this.setActive(page);
  }

  updateActive() {
    const maxScroll = Math.max(this.rail.scrollWidth - this.rail.clientWidth, 1);
    this.setActive(this.rail.scrollLeft >= maxScroll / 2 ? 1 : 0);
  }

  setActive(page) {
    this.buttons.forEach((button, index) => {
      const active = index === page;
      button.classList.toggle('is-active', active);
      button.toggleAttribute('aria-current', active);
    });
  }
}

/* ------------------------------------------------------------------ */
/* Delivery window                                                     */
/* ------------------------------------------------------------------ */

class LpDelivery extends HTMLElement {
  connectedCallback() {
    const min = parseInt(this.dataset.minDays || '', 10);
    const max = parseInt(this.dataset.maxDays || '', 10);
    const out = this.querySelector('[data-lp-delivery-dates]');
    if (!out || Number.isNaN(min) || Number.isNaN(max)) return;

    const fmt = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      month: 'short',
      day: 'numeric',
    });
    const from = new Date();
    from.setDate(from.getDate() + min);
    const to = new Date();
    to.setDate(to.getDate() + max);
    out.textContent = min === max ? fmt.format(from) : `${fmt.format(from)} – ${fmt.format(to)}`;
  }
}

/* ------------------------------------------------------------------ */
/* Ships-today order cutoff                                            */
/* ------------------------------------------------------------------ */

class LpShipCutoff extends HTMLElement {
  connectedCallback() {
    this.labelEl = this.querySelector('[data-ship-label]');
    this.timerEl = this.querySelector('[data-ship-timer]');
    if (!this.labelEl || !this.timerEl) return;
    this.tz = this.dataset.timezone || 'America/New_York';
    this.cutoffHour = parseInt(this.dataset.cutoffHour || '14', 10);
    this.cutoffMinute = parseInt(this.dataset.cutoffMinute || '0', 10);
    try {
      this.tick();
    } catch {
      return; // bad timezone — stay hidden rather than show wrong info
    }
    this.hidden = false;
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  /** Current wall-clock parts in the warehouse timezone. */
  nowInTz() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.tz,
      hour12: false,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }).formatToParts(new Date());
    const get = (t) => parseInt(parts.find((p) => p.type === t).value, 10);
    return {
      h: get('hour') % 24,
      m: get('minute'),
      s: get('second'),
    };
  }

  tick() {
    const now = this.nowInTz();
    const nowSecs = now.h * 3600 + now.m * 60 + now.s;
    const cutoffSecs = this.cutoffHour * 3600 + this.cutoffMinute * 60;
    const beforeCutoff = nowSecs < cutoffSecs;
    const remaining = beforeCutoff ? cutoffSecs - nowSecs : 86400 - nowSecs + cutoffSecs;

    const shipDate = new Date();
    if (!beforeCutoff) shipDate.setDate(shipDate.getDate() + 1);
    const dateLabel = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      timeZone: this.tz,
      month: 'short',
      day: 'numeric',
    }).format(shipDate);

    const prefix = beforeCutoff ? this.dataset.labelToday : this.dataset.labelTomorrow;
    this.labelEl.textContent = `${prefix} ${dateLabel}`;

    const h = Math.floor(remaining / 3600);
    const m = Math.floor((remaining % 3600) / 60);
    const s = remaining % 60;
    this.timerEl.textContent = `${h}hr ${m}mins ${s}sec`;
  }
}

/* ------------------------------------------------------------------ */

if (!customElements.get('lp-ship-cutoff')) customElements.define('lp-ship-cutoff', LpShipCutoff);
if (!customElements.get('lp-countdown')) customElements.define('lp-countdown', LpCountdown);
if (!customElements.get('lp-offer-cards')) customElements.define('lp-offer-cards', LpOfferCards);
if (!customElements.get('lp-countup')) customElements.define('lp-countup', LpCountup);
if (!customElements.get('lp-video-card')) customElements.define('lp-video-card', LpVideoCard);
if (!customElements.get('lp-story-carousel')) customElements.define('lp-story-carousel', LpStoryCarousel);
if (!customElements.get('lp-delivery')) customElements.define('lp-delivery', LpDelivery);
